import css_cunchwj5k4 from './css/VideoPlayer.css';
import Div from './Div.js';
import LayoutGrid from './LayoutGrid.js';
import GetPrivateData from '../Classes/GetPrivateData.js';
import MediaGroup from './MediaGroup.js';
import ContentPlayer from './ContentPlayer.js';
import AdPlayer from './AdPlayer.js';
import Log from '../Classes/Log.js';
import PlayerStatus from '../Classes/PlayerStatus.js';
import eventOptions from '../Functions/eventOptions.js';
import urlQueryToObject from '../Functions/urlQueryToObject.js';
import ElementFinder from '../Classes/ElementFinder.js';
import EngagementLogger from '../Classes/EngagementLogger.js';

/** @namespace config */

const css = css_cunchwj5k4;

export default class VideoPlayer extends Div
{
	constructor(parentElement)
	{
		super(parentElement);

		config.player = this;

		config.videoFinder = new ElementFinder
		(
			'video',
			video =>
			this.layoutGrid.mediaGroupContainer.activeMediaGroup.adPlayer.element.contains(video) ||
			this.layoutGrid.mediaGroupContainer.preloadMediaGroup.adPlayer.element.contains(video)
		);
		config.videoFinder.track(document);

		if(config.feed.redSkin)
		{
			this.element.classList.add('colour');
		}

		if(config.feed.noTitle)
		{
			this.element.classList.add('noTitle');
		}

		const privateData =
		{
			playing: false,
			preloadIndex: null,
			activeIndex: null,
			// userEngaged = null is implicitly disengaged because haven't engaged yet
			// = true is explicitly engaged due to action
			// = false is explicitly disengaged due to float close button
			userEngaged: null,
			hasBeenVisible: this.isVisible,
			activeLoadCount: 0,
			adStarted: false,
			readyToPlayEventFired: false,
			// comScorePromise is fulfilled once ComScore has succeeded OR failed
			comScorePromise: config.scripts.comScore.catch( () => {} )
		};

		getPrivateData.for(this,privateData);

		logEvents.call(this);

		let savedClassName = '';
		new MutationObserver
		(
			entries =>
			{
				entries.forEach
				(
					() =>
					{
						const oldClass = savedClassName.split(' ');
						const newClass = this.element.className.split(' ');
						const removed = oldClass.filter( v => v && newClass.indexOf(v) === -1 );
						const added = newClass.filter( v => v && oldClass.indexOf(v) === -1 );

						savedClassName = this.element.className;

						if(added.length || removed.length)
						{
							// console.log('ClassName changed to: %c'+savedClassName,'color:red');
						}

						removed.forEach( v => config.divTag.classList.remove('s2n'+v[0].toUpperCase()+v.slice(1)) );
						added.forEach( v => config.divTag.classList.add('s2n'+v[0].toUpperCase()+v.slice(1)) );
					}
				);
			}
		)
		.observe
		(
			this.element,
			{
				// ie11 bug, attributes needs to be specified despite attributeFilter implying attribute:true in the spec
				attributes: true,
				attributeFilter:['class']
			}
		);

		config.feed.isBarker = config.feed.playerType === 'BARKER' || config.divTag.hasAttribute('data-forcebarker');

		if(config.feed.isBarker)
		{
			new ResizeObserver
			(
				entries =>
				{
					const width = entries.pop().contentRect.width;
					let barkerAllowed;
					let barkerHeight;
					switch (config.divTag.getAttribute('data-size'))
					{
						case '220':
							barkerAllowed = width >= 650;
							barkerHeight = '220';
							break;
						default:
						case '400':
							barkerAllowed = width >= 700;
							barkerHeight = '265';
							break;
						case '500':
							barkerAllowed = width >= 750;
							barkerHeight = '313';
							break;
					}
					if(barkerAllowed)
					{
						config.player.element.classList.add('barker');
						config.divTag.style.height = `${barkerHeight}px`;
					}
					else
					{
						config.player.element.classList.remove('barker');
						config.divTag.style.height = '';
					}
				}
			)
			.observe(config.divTag);
		}

		this.element.classList.add('paused');

		this.layoutGrid = new LayoutGrid(this);

		function onVisibilityChange()
		{
			config.ac_id.pageHidden = document.hidden;

			if(config.feed.pauseWhenHidden)
			{
				const isPlaying = config.player.playing();

				if(document.hidden === isPlaying)
				{
					if(isPlaying)
					{
						config.visibilityPause = true;
						config.player.playing(false);
					}
					else if(config.visibilityPause)
					{
						config.visibilityPause = false;
						config.player.playing(true);
					}
				}
			}
		}

		if(typeof document.visibilityState === 'string' && typeof document.hidden === 'boolean')
		{
			document.addEventListener("visibilitychange", onVisibilityChange);
		}
		onVisibilityChange();

		this.element.addEventListener
		(
			'mediaGroupContainerVolumechange',
			event =>
			{
				if(event.detail.previous.volume !== event.detail.volume)
				{
					console.log('Volume Changed from', 1*event.detail.previous.volume.toFixed(3), 'to', 1*event.detail.volume.toFixed(3));
				}
				if(event.detail.previous.muted !== event.detail.muted)
				{
					console.log('Muted Changed from', event.detail.previous.muted, 'to', event.detail.muted);
				}
				config.ac_id.sound = !event.detail.muted && event.detail.volume;
			}
		);

		this.layoutGrid.titleBarContainer.floatCloseButton.element.addEventListener
		(
			'transitionend',
			() =>
			{
				this.closeFloat({close: true, report: true});
			}
		);

		this.layoutGrid.titleBarContainer.floatCloseButton.element.addEventListener
		(
			'click',
			() =>
			{
				this.closeFloat({disengage:true});
			}
		);

		// videojs has a touch handler which will attempt to play a video when you click on the poster.
		// we need to cancel this event and let the click handler that we've installed handle the starting of the player instead
		// this keeps the ad system in sync with the play status of the player
		this.layoutGrid.mediaGroupContainer.element.addEventListener
		(
			'touchstart',
			event =>
			{
				let target = event.target;
				do
				{
					if
					(
						// cancel touch events on these components
						target.classList.contains('vjs-big-play-button') ||
						target.classList.contains('vjs-poster')
					)
					{
						break;
					}

					target = target.parentElement;
				}
				while(target);

				if(target)
				{
					// first we need to prevent the event from reaching videojs
					event.stopPropagation();
					// then we need to prevent ANDROID's default action which is to send a click.
					// We don't want to double click the component on android and single click on ios.
					event.preventDefault();

					// now that the event has been stopped, we need to actually start the player
					event.target.click();
				}
			},
			eventOptions({capture:true})
		);

		this.layoutGrid.mediaGroupContainer.element.addEventListener
		(
			'click',
			event =>
			{
				let target = event.target;
				while(target)
				{
					if(target.classList.contains('vjs-poster') || target.classList.contains('vjs-big-play-button'))
					{
						event.preventDefault();
						this.playing(true);
						return;
					}
					target = target.parentElement;
				}
			},
			eventOptions({capture:true})
		);

		// when the user clicks, send out a userengaged event
		// so that the elements involved can cancel if they want to do so.
		this.element.addEventListener
		(
			'click', event =>
			{
				// console.error('DISPATCH ENGAGEMENT REQUEST');
				this.dispatchEvent('userengaged',{target:event.target});
			},
			eventOptions({capture:true})
		);

		// when the user engaged event comes back in the bubble, set userengaged.
		// floatCloseButton and adCompanionClose and possibly others will listen to this
		// event and stop it from propagating if the user should not count as engaged
		this.element.addEventListener
		(
			'userengaged', event =>
			{
				// console.error('ENGAGEMENT ALLOWED');
				privateData.userEngaged = true;
				// if we are not engaged, check if we should unmute
				// not all engagements count as unmute events.
				// Any mute buttons or float close buttons are not unmute events
				// console.error('DISPATCH UNMUTE REQUEST');
				this.dispatchEvent('unmute',{target:event.target});
			}
		);

		this.element.addEventListener
		(
			'unmute', () =>
			{
				// console.error('UNMUTE ALLOWED');
				this.layoutGrid.mediaGroupContainer.activeMediaGroup.muted(false);
			}
		);

		this.layoutGrid.playlistContainer.playlist.items.element.addEventListener
		(
			'click', event =>
			{
				let target = event.target;
				while(target)
				{
					if(target.classList.contains('item'))
					{
						let index = Array.from(target.parentElement.children).indexOf(target);
						config.ac_id.userLoaded = true;
						privateData.userEngaged = true;
						this.load(index);
						this.playing(true);
						return;
					}
					target = target.parentElement;
				}
			}
		);

		this.layoutGrid.titleBarContainer.menuButton.element.addEventListener
		(
			'click',
			() =>
			{
				if(this.playing())
				{
					this.playing( false );
				}
				else
				{
					privateData.userEngaged = true;
					this.playing( true );
				}
			}
		);

		this.layoutGrid.overlayContainer.overlay.element.addEventListener
		(
			'click',
			() =>
			{
				privateData.userEngaged = true;
				this.playing(true);
			}
		);

		this.element.addEventListener('contentTimeupdate', (event) =>
		{
			const detail = event.detail;
			const nextIndex = (privateData.activeIndex + 1) % config.playlist.items.length;
			if (privateData.preloadIndex !== nextIndex && (detail.duration - detail.currentTime < 10))
			{
				//this.load(nextIndex, true);
			}
		});

		this.element.addEventListener('mediaGroupComplete', () =>
		{
			const nextIndex = privateData.activeIndex+1;
			this.load(nextIndex);
			const playNext =
			(
				() =>
				{
					// if we aren't looping the playlist, don't play
					if(nextIndex === config.playlist.items.length && !config.feed.loopPlaylist)
					{
						return false;
					}

					// if we aren't play on visible, play
					if(!config.feed.playOnVisible)
					{
						return true;
					}

					// if playOnVisible resume has been disabled, don't play
					if(config.feed.playOnVisibleNoResume)
					{
						// console.warn('PLAY ON VISIBLE NO RESUME');
						return false;
					}

					// console.warn('PLAY ON VISIBLE WAITING');

					// play once we are in view
					getPlayOnVisiblePromise.call(this).then
					(
						() =>
						{
							config.ac_id.pov = true;
							this.playing(true)
						},
						()=>{}
					);

					return null;
				}
			)
			();

			privateData.playing = !!playNext;

			if(playNext === true)
			{
				this.playing(true);
			}
			else if (playNext === false)
			{
				this.element.classList.remove('ending');
				this.element.classList.add('paused');
			}
		});

		this.element.addEventListener
		(
			'adActive',
			() =>
			{
				this.element.classList.add('adActive');
			}
		);

		this.element.addEventListener
		(
			'adInactive',
			() =>
			{
				this.element.classList.remove('adActive');
			}
		);

		this.element.addEventListener
		(
			'mediaGroupPlay',
			() =>
			{
				this.element.classList.remove('ending');
				this.element.classList.remove('paused');
				this.element.classList.remove('scrubbing');
				this.element.classList.add('hasStarted');
				privateData.playing = true;
			}
		);
		this.element.addEventListener
		(
			'mediaGroupPause',
			event =>
			{
				if(event.detail && event.detail.scrubbing)
				{
					this.element.classList.add('scrubbing');
				}
				else if(event.detail && event.detail.ended)
				{
					this.element.classList.add('ending');
				}
				else
				{
					this.element.classList.add('paused');
					privateData.playing = false;
				}
			}
		);

		this.element.addEventListener
		(
			'adPlay',
			() => config.log.INV()
		);

		this.element.addEventListener
		(
			'contentPlay',
			() => config.log.PLAY()
		);

		this.layoutGrid.titleBarContainer.prepend.value = config.feed['titlePrepend'];

		config.playlistCache[config.key].then
		(
			playlist =>
			{
				if(playlist.type === 'SMART')
				{
					this.element.classList.add('smartMatch');
				}

				if(config.feed.enableResonate)
				{
					resonateAnalytics['track']
					(
						"Video Play",
						{
							"Publisher Site": config.divTag.ownerDocument.location.host,
							"Level1": playlist.items[0]['level1'],
							"Level2": playlist.items[0]['level2'],
							"Level3": playlist.items[0]['level3']
						}
					);
				}

				config.playlist = playlist;
				config.playerStatus = new PlayerStatus();

				let lastHeight = null;
				if(config.playerStatus.values[5] === 'A' && urlQueryToObject(location.search,true)['v'] === '2')
				{
					new ResizeObserver
					(
						entries =>
						{
							const height = entries.pop().contentRect.height;
							if(lastHeight !== height)
							{
								lastHeight = height;
								window.parent.postMessage
								(
									{
										sentinel: 'amp',
										type: 'embed-size',
										height: height+'px'
									},
									'*'
								);
							}
						}
					)
					.observe(this.layoutGrid.element);
				}

				config.log = new Log();

				// once we know the result of ComScore, load. ComScore depends on the load event.
				privateData.comScorePromise.then
				(
					() => playlist.items && playlist.items.length && this.load(0)
				);

				const delayPlayOnVisibleTillScrollPromise = new Promise
				(
					resolve =>
					{
						if(!config.feed.scrollDelay)
						{
							resolve();
							return;
						}

						config.gestures.subscribe
						(
							function observer()
							{
								config.gestures.unsubscribe(observer);
								resolve();
							}
						);

						function onScroll()
						{
							let target = window;
							while(target)
							{
								target.removeEventListener('scroll', onScroll);
								target = target.frameElement && target.parent;
							}
							resolve();
						}

						let target = window;
						while(target)
						{
							target.addEventListener('scroll', onScroll);
							target = target.frameElement && target.parent;
						}
					}
				);

				// play if we are in view
				const playOnVisiblePromise = getPlayOnVisiblePromise.call(this);

				let autoplayPromise;
				if(config.feed.autoplay)
				{
					 autoplayPromise = Promise.resolve();
				}
				else
				{
					autoplayPromise = Promise.reject('Click to play');
				}

				Promise.all([autoplayPromise,playOnVisiblePromise,delayPlayOnVisibleTillScrollPromise])
				.then
				(
					([,pov]) =>
					{
						if(pov)
						{
							config.ac_id.pov = true;
						}
						else
						{
							config.ac_id.autoplay = true;
						}
						// if pov promise is in visible and floatOnLoad is also configured, then
						// don't try to start the player as float on load will do that for us
						if(pov && config.feed.floatOnLoad)
						{
							return;
						}
						this.playing(true);
					},
					// click to play
					() => {}
				);

				if(config.feed.float)
				{
					config.player.element.addEventListener
					(
						'mediaGroupPlay',
						() =>
						{
							floatPlayer.call(this);
						}
					);

					new Promise
					(
						resolve =>
						{
							if(!config.feed.floatOnLoadScrollDelay)
							{
								resolve();
								return;
							}

							const onScroll = function()
							{
								if(config.divTag.ownerDocument.defaultView.scrollY >= config.feed.floatOnLoadScrollDelay)
								{
									config.divTag.ownerDocument.removeEventListener('scroll',onScroll);
									resolve();
								}
								else
								{
									// function is bound via bind(this)
									// noinspection JSPotentiallyInvalidUsageOfClassThis
									if(this.isVisible)
									{
										resolve();
									}
								}
							}.bind(this);

							config.divTag.ownerDocument.addEventListener('scroll',onScroll);
							onScroll();
						}
					)
					.then
					(
						() =>
						{
							new MutationObserver
							(
								(entries, observer) =>
								{
									if(this.isVisible)
									{
										if(this.element.classList.contains('float'))
										{
											this.element.classList.remove('float');
										}
										// float is only enabled once the player has been 50% in view
										privateData.hasBeenVisible = true;
									}
									else
									{
										if(privateData.hasFloatedOnce && config.feed.floatOnce)
										{
											observer.disconnect();
											return;
										}

										if(privateData.playing)
										{
											floatPlayer.call(this);
										}
									}
								}
							)
							.observe
							(
								config.divTag,
								{
									// ie11 bug, attributes needs to be specified despite attributeFilter implying attribute:true in the spec
									attributes: true,
									attributeFilter: ['data-visible']
								}
							);

							if(config.feed.floatOnLoad)
							{
								config.ac_id.outsideViewPort = false;
								floatPlayer.call(this,true);
								this.playing(true);
							}
						}
					)
				}
			}
		);

		config.feed['enableEngagementLogging'] &&
		this.element.addEventListener
		(
			'contentLoad',
			() =>
			{
				const item = config.player.currentItem;
				privateData.engagementLogger = new EngagementLogger
				(
					item['SM_ID'],
					item['loadCount'],
					this.layoutGrid.mediaGroupContainer.activeMediaGroup.contentPlayer.proxy,
					window
				);
			}
		)
	}

	get isVisible()
	{
		return config.divTag.getAttribute('data-visible') >= 50;
	}

	closeFloat(options)
	{
		const privateData = getPrivateData.for(this);

		if(options.disengage)
		{
			privateData.userEngaged = false;
			this.layoutGrid.mediaGroupContainer.activeMediaGroup.muted(true);
		}

		if(options.close)
		{
			this.element.classList.remove('float');
		}

		if(options.report)
		{
			if(privateData.adStarted)
			{
				// closed while ads are active
				config.log.CLOSE('AD');
			}
			else if(privateData.playing)
			{
				// closed while playing
				config.log.CLOSE('PL');
			}
			else
			{
				// todo: trigger true if looping playlist has stopped
				// if(false)
				// {
				// 	// closed while paused at end of video
				// 	config.log.CLOSE('ID');
				// }
				// else
				{
					// closed while paused
					config.log.CLOSE('PA');
				}
			}
		}
	}

	get cssText()
	{
		return css;
	}

	get currentItem()
	{
		const privateData = getPrivateData.for(this);
		return Object.assign({},config.playlist.items[privateData.activeIndex || 0]);
	}

	load(index, preload)
	{
		index = index % config.playlist.items.length;

		const item = config.playlist.items[index];

		const privateData = getPrivateData.for(this);
		const mediaGroupContainer = this.layoutGrid.mediaGroupContainer;

		if(preload)
		{
			privateData.preloadIndex = index;
			mediaGroupContainer.preloadMediaGroup.load(index);
			return;
		}

		if(privateData.activeLoadCount !== 0)
		{
			config.playerStatus.next();
			config.ac_id.nextPlay = true;
			if(config.ac_id.clickToPlay)
			{
				config.ac_id.autoplay = true;
			}
		}

		const loadCount = privateData.activeLoadCount = (privateData.activeLoadCount || 0) + 1;
		item['loadCount'] = (item['loadCount'] || 0) + 1;

		this.dispatchEvent
		(
			'load',
			{
				detail:
				{
					loadCount,
					playlistIndex: index,
					mediaId: item['SM_ID'],
					mediaLength: item['SM_M_LENGTH'],
					mediaKeywords: (item['S_tags'] || '').split(',').filter(v=>v).map(v => v.trim()),
					mediaCategory: [item['level1'],item['level2'],item['level3']],
					mediaTitle: item['S_headLine'],
					mediaPublisher: item['STL_leagueNameShort']
				}
			}
		);

		privateData.engagementLogger && privateData.engagementLogger.destroy();
		privateData.engagementLogger = null;

		config.log.reset();

		privateData.activeIndex = index;
		const itemsElement = this.layoutGrid.playlistContainer.playlist.items.element;
		itemsElement.querySelector('.active').classList.remove('active');
		itemsElement.children[index].classList.add('active');

		if(privateData.preloadIndex === index)
		{
			privateData.preloadIndex = null;
			mediaGroupContainer.setActive(mediaGroupContainer.preloadMediaGroup)
		}
		else
		{
			mediaGroupContainer.activeMediaGroup.load(index);
		}

		this.layoutGrid.mediaGroupContainer.activeMediaGroup.contentPlayer.companionAd.clear();
		this.layoutGrid.titleBarContainer.title.value = item['configuration'].title;
		this.layoutGrid.playlistContainer.playlist.title.value = item['configuration'].title;
		this.layoutGrid.overlayContainer.overlay.value = item;
	}

	playing(...value)
	{
		if(value.length)
		{
			const comScorePromise = getPrivateData.for(this).comScorePromise;
			const result = comScorePromise.then
			(
				() =>
				{
					return this.layoutGrid.mediaGroupContainer.activeMediaGroup.playing(...value);
				}
			);
			this.dispatchEvent('readyToPlay');
			this.element.classList.add('waitingForPlay');
			result.finally
			(
				() =>
				{
					return this.element.classList.remove('waitingForPlay');
				}
			);
			return result;
		}
		else
		{
			return this.layoutGrid.mediaGroupContainer.activeMediaGroup.playing();
		}
	}
}

function floatPlayer(ignoreRestrictions = false)
{
	const privateData = getPrivateData.for(this);

	if( !ignoreRestrictions )
	{
		if(!privateData.hasBeenVisible)
		{
			return;
		}
		if(privateData.userEngaged === false )
		{
			// console.warn('USER NOT ENGAGED', privateData);
			return;
		}
	}

	// NEVER float an already visible player
	if(this.isVisible)
	{
		return;
	}

	if(!privateData.hasFloatedOnce)
	{
		if(config.globals.lastPlayerToFloatForFirstTime && config.globals.lastPlayerToFloatForFirstTime !== this)
		{
			config.globals.lastPlayerToFloatForFirstTime.closeFloat({disengage: true, close: true});
			config.globals.lastPlayerToFloatForFirstTime = null;
		}
		config.globals.lastPlayerToFloatForFirstTime = this;
	}

	this.element.classList.add('float');
	privateData.hasFloatedOnce = true;
}

const getPrivateData = new GetPrivateData(VideoPlayer);

VideoPlayer.eventList = ['load','readyToPlay'];

function getPlayOnVisiblePromise()
{
	// resolves as 'play on visible is set and triggered'
	// rejects if player is already playing for any reason
	const result = new Promise
	(
		(resolve,reject) =>
		{
			if(!config.feed.playOnVisible)
			{
				resolve(false);
			}

			new MutationObserver
			(
				(entries, observer) =>
				{
					if(config.divTag.getAttribute('data-visible') >= config.feed.playOnVisiblePercent || config.player.element.classList.contains('float'))
					{
						resolve(true);
						observer.disconnect();
					}
				}
			)
			.observe
			(
				config.divTag,
				{
					// ie11 bug, attributes needs to be specified despite attributeFilter implying attribute:true in the spec
					attributes: true,
					attributeFilter: ['data-visible']
				}
			);

			config.player.element.addEventListener
			(
				'mediaGroupPlay',
				function onPlay()
				{
					config.player.element.removeEventListener('mediaGroupPlay',onPlay);
					reject();
				}
			)
		}
	);

	config.divTag.setAttribute('data-visible',config.divTag.getAttribute('data-visible'));

	return result;
}

function logEvents()
{
	const privateData = getPrivateData.for(this);
	// log all of the events from the MediaGroup, ContentPlayer, and AdPlayer classes
	[]
	.concat
	(
		MediaGroup
		.eventList
		.map(v => 'mediaGroup'+v[0].toUpperCase()+v.slice(1))
	)
	.concat
	(
		ContentPlayer
		.eventList
		.map(v => 'content'+v[0].toUpperCase()+v.slice(1))
	)
	.concat
	(
		AdPlayer
		.eventList
		.map(v => 'ad'+v[0].toUpperCase()+v.slice(1))
	)
	.concat
	(
		VideoPlayer.eventList
	)
	// don't log the timeupdate events. Way too many of them
	.filter
	(
		v => v.slice(-10) !== 'Timeupdate'
	)
	// todo: create an s2nPlayerEvent and publish outside of the iframe as part of the api
	.forEach
	(
		type =>
		{
			this.element.addEventListener
			(
				type,
				event =>
				{
					let apiEvent = true;
					let exposeEvent = false;

					switch (type)
					{
						case 'load':
							privateData.adStartEventExposed = false;
							break;
						case 'adStart':
							privateData.adStarted = true;
							if(!privateData.adStartEventExposed)
							{
								privateData.adStartEventExposed = true;
								exposeEvent = true;
							}
							break;
							// don't sent the adPlay event that occurs before the started event
						case 'adPlay':
							if(privateData.adStarted === false)
							{
								apiEvent = false;
							}
							break;
							// don't sent the adPause event that occurs after the finish event
						case 'adPause':
							if(privateData.adStarted === false)
								apiEvent = false;
							break;
						case 'adFinish':
							privateData.adStarted = false;
							break;
						case 'contentPlay':
							break;
						case 'contentPause':
							break;
						case 'contentFinish':
							break;
						case 'readyToPlay':
							if(!privateData.readyToPlayEventFired)
							{
								privateData.readyToPlayEventFired = true;
								exposeEvent = true;
								config.log.RTP();
							}
							else
							{
								apiEvent = false;
							}
							break;
						default:
							apiEvent=false;
					}

					const baseEventMessage =
					{
						type: 's2nEvent',
						playerKey: config.key,
						s2nEvent: type,
					};

					const apiEventMessage = Object.assign
					(
						JSON.parse(JSON.stringify(baseEventMessage)),
						event.detail
					);

					let target = window;

					if(apiEvent)
					{
						target.postMessage(apiEventMessage,'*');
					}

					if(exposeEvent)
					{
						target = (target.parent === target ? null : target.parent);

						while(target)
						{
							target.postMessage(baseEventMessage,'*');
							target = (target.parent === target ? null : target.parent);
						}

					}

					// console.log(`S2N Event - ${type}`, event.detail);
				}
			);
		}
	);
}
